import { ReactNode, useState } from 'react'

import { IContinuousRoute } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'
import {
  useAddQRAAssessmentMutation,
  useGetQRAAssessmentsQuery
} from 'app/services/api/qra/assessments'

import { selectSavedFilter } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import { IStateAssessment } from '..'
import { WISAssessmentStatusCode } from '../WISAssessmentStatusCode'
import RiskManagementViewWithRef from './RiskManagementView'
import { WISWrapperType } from './types'
import { useOnCreateValidator } from './utils/useOnCreateWISValidator'
import { selectScenario } from './whatIfScenarioSelectors'
import { setSelectedScenarioSync } from './whatIfScenarioSlice'
import WISModal from './WISModal'

const CreateWISWrapper = ({
  stateId,
  setViewMode,
  scenarioOptions,
  closeModal,
  isModalOpen,
  wisName,
  setWisName
}: WISWrapperType) => {
  const dispatch = useAppDispatch()
  const [assessmentRan, setAssessmentRan] = useState<
    IStateAssessment | undefined
  >(undefined)

  const [isScenarioNameEmpty, setIsScenarioNameEmpty] = useState<boolean>(false)
  const [wasScenarioSelected, setWasScenarioSelected] = useState<boolean>(false)

  const localFilterProfile = useAppSelector(selectSavedFilter)
  const scenario = useAppSelector(selectScenario)

  const { currentData: assessmentsData } = useGetQRAAssessmentsQuery()

  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const [createAssessment, { isCreateAssessmentLoading }] =
    useAddQRAAssessmentMutation({
      selectFromResult: (result) => ({
        isCreateAssessmentLoading: result.isLoading
      })
    })

  const runWIS = () => {
    let selectedRoutes: IContinuousRoute[] = []

    if (localFilterProfile.segments.length !== 0) {
      selectedRoutes = localFilterProfile.segments.map(
        (segment: IContinuousRoute) => ({
          ...segment,
          id: 0
        })
      )
    } else {
      // REVIEW: Why do we have to map for system scope but not local profile?
      //         Check `IContinousRoute` interface doc
      selectedRoutes =
        systemScopeFilterProfile?.continuousRoutes.map(
          (line: IContinuousRoute) => ({
            id: 0,
            rowId: line.rowId,
            continuousRouteId: line.id,
            pipelineRouteId: line.id,
            name: line.name,
            fromMeasure: line.fromMeasure,
            initialFromMeasure: line.fromMeasure,
            initialToMeasure: line.toMeasure,
            toMeasure: line.toMeasure
          })
        ) ?? []
    }

    const stagingAssessment: Partial<IStateAssessment> = {
      savedStateId: stateId,
      name: wisName,
      statusCode: WISAssessmentStatusCode.New,
      typeCode: 'WIS',
      assessmentContinuousRoutes: selectedRoutes,
      assessmentRiskLevels: localFilterProfile.riskLevels ?? [],
      assessmentClassLocations: localFilterProfile.classLocations ?? [],
      assessmentConsequenceLevels: localFilterProfile.hmca ?? [],
      assessmentRiskModels: [{ riskModelId: '1' }],
      assessmentScenarios: [
        {
          id: 0,
          name: scenario.name,
          rules: scenario.rules
        }
      ]
    }

    const wisAssessmentPromise: Promise<IStateAssessment> =
      createAssessment(stagingAssessment).unwrap()

    return wisAssessmentPromise.then((assessment) => {
      setAssessmentRan(assessment)
    })
  }

  const existingAssessmentNames =
    assessmentsData?.map((assessment: IStateAssessment) => assessment.name) ||
    []

  const [isScenarioNameValid, scenarioNameValidationMessage] =
    useOnCreateValidator(wisName, existingAssessmentNames, isScenarioNameEmpty)

  // Add this logic to disable validation during loading
  const shouldShowValidation = !isCreateAssessmentLoading && !assessmentRan

  const renderContent: ReactNode = (
    <RiskManagementViewWithRef
      wisName={wisName}
      scenarioOptions={scenarioOptions}
      setWisName={setWisName}
      title='Create New What-If Scenario'
      setViewMode={setViewMode}
      setIsScenarioNameEmpty={setIsScenarioNameEmpty}
      scenarioNameValidationMessage={
        shouldShowValidation ? scenarioNameValidationMessage : ''
      }
      isScenarioNameValid={shouldShowValidation ? isScenarioNameValid : true}
      setWasScenarioSelected={setWasScenarioSelected}
    />
  )

  const canConfirm =
    wasScenarioSelected && isScenarioNameValid && wisName.length > 0

  const handleClose = (pathnameOverride?: string) => {
    setWisName('')
    setAssessmentRan(undefined)
    setIsScenarioNameEmpty(false)
    dispatch(setSelectedScenarioSync({ id: 0, name: '', rules: [] }))
    closeModal(pathnameOverride)
  }

  return (
    <WISModal
      onConfirm={runWIS}
      renderContent={renderContent}
      closeModal={handleClose}
      isModalOpen={isModalOpen}
      canConfirm={canConfirm}
      assessmentRan={assessmentRan}
      footerApplyLabel='Run Scenario'
      onCancel={handleClose}
      isLoading={isCreateAssessmentLoading}
    />
  )
}

export default CreateWISWrapper
